import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ForgeDialog } from "@/components/forge/ForgeDialog";
import { Icon } from "@/components/forge/Icon";
import { cn } from "@/lib/cn";

/**
 * Every role against every permission.
 *
 * ── Why this exists beside the per-role grid ──────────────────────────────
 * The role detail answers "what can this role do". This answers the other
 * question, which is the one an access review actually asks: "who can do this".
 * Reading a feature across the row is how somebody notices that four roles can
 * close a work order.
 *
 * ── A grid is a table, and a table needs headers in both directions ───────
 * The first column is `<th scope="row">` and the role columns are
 * `<th scope="col">`, so a screen reader announces "Approve work orders,
 * Reliability Engineer, granted" rather than reading a wall of ticks. The
 * prototype's is a div grid with no headers at all, which is unreadable by
 * anything that is not a pair of eyes.
 *
 * ── The tick is not the only signal ───────────────────────────────────────
 * Colour alone fails for a colour-blind reader and in print. Granted is a check
 * glyph and withheld is an em dash, both with a `title` naming the state.
 *
 * ── Built from the real catalogue ─────────────────────────────────────────
 * It used to draw the navigation rail against invented feature names. The data
 * it needed turned out to be exactly what the API already serves: modules with
 * their permissions down the side, roles across the top, and a role's
 * `permission_ids` deciding each cell. Nothing about the table changed — only
 * where the two axes come from.
 *
 * `permission_ids` is a list rather than a map, so it is turned into a Set per
 * role once here instead of a linear scan in every one of the ~170 cells.
 */
export function PermissionMatrixDialog({ open, roles, modules, onClose }) {
  const { t } = useTranslation(["app", "common"]);

  const grantedBy = useMemo(
    () => new Map(roles.map((role) => [role.id, new Set(role.permissionIds)])),
    [roles],
  );

  return (
    <ForgeDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="xl"
      /**
       * The modal hugs the table, rather than the table filling the modal.
       *
       * `DialogContent` ships `w-full`, so a dialog always occupies its whole
       * max-width — 1100px at this size. With four roles the matrix needs
       * around nine hundred, and the leftover turned into a band of empty panel
       * down the right-hand side.
       *
       * `w-auto` lets it size to its content. `max-w` still caps it, so a
       * twenty-role account fills the screen and scrolls rather than growing
       * past it, and `min-w` stops a two-role account collapsing the dialog
       * around its own title.
       *
       * Both are `sm:`-prefixed to match the max-width they are overriding —
       * below that breakpoint the dialog is full-bleed and none of this applies.
       */
      className="w-auto sm:min-w-[34rem]"
      title={t("settings.roles.matrixTitle")}
      description={t("settings.roles.matrixSubtitle")}
      /**
       * ONE scroll container, not two.
       *
       * `ForgeDialog`'s body scrolls vertically by default, which suits a column
       * of fields. A matrix scrolls in BOTH directions and pins its own headers
       * while it does — and the two arrangements did not compose: the table sat
       * in a wrapper that scrolled inside a body that also scrolled, so a drag
       * moved whichever one it started over, and `position: sticky` resolved
       * against the inner box while the outer one slid out from under it. The
       * headers came unstuck exactly when they were doing their job.
       *
       * The fix is to have one scroller rather than to nest them better. The
       * body becomes it: `overflow-auto` for both axes, the cap the wrapper used
       * to hold, and no padding so the table reaches the dialog's edges and its
       * sticky first column pins against them. The wrapper is gone.
       *
       * Sticky then resolves against the body, which is the nearest scrollable
       * ancestor — the same relationship as before, minus the box in between.
       *
       * The value is spelled as an arbitrary one on purpose. `max-h-none` does
       * NOT displace `max-h-[min(60vh,32rem)]`: tailwind-merge cannot parse the
       * comma inside that arbitrary value, so both classes survive and which
       * wins depends on their order in the built sheet. Any concrete value
       * merges correctly; `none` is the one that does not.
       */
      bodyClassName="max-h-[min(72vh,44rem)] overflow-auto p-0"
      /* No footer. A dialog that only closes does not need a row of chrome to
         say so — the header's own × does it, and Escape and a click outside
         both already work through Radix. The bar was costing a strip of
         vertical space on the one screen that wants all of it for rows. */
    >
      {/* `w-full` — and this only works because the DIALOG is sized to content.
 
          The slack has to go somewhere, and neither setting fixes it alone. A
          full-width table inside a fixed-width dialog hands every spare pixel to
          the widest column, which is the permission name: the names sit far
          left, the ticks far right, and the middle is empty. Sizing the TABLE to
          content closes that gap and moves the same slack to the right-hand edge
          as a band of bare panel.
 
          So the dialog gives up its fixed width instead (see `className` above).
          It hugs the table, the table fills it, and there is no slack left to
          fall anywhere. Change one of these two and the other has to change with
          it. */}
      <table className="w-full border-collapse">
        <caption className="sr-only">
          {t("settings.roles.matrixCaption")}
        </caption>
        <thead>
          <tr>
            <th
              scope="col"
              className="eyebrow sticky start-0 top-0 z-20 bg-sunken px-3.5 py-2.5 text-start"
            >
              {t("settings.roles.feature")}
            </th>
            {roles.map((role) => (
              <th
                key={role.id}
                scope="col"
                className="eyebrow sticky top-0 z-10 min-w-[7rem] bg-sunken px-3 py-2.5 text-center align-bottom"
              >
                {/* The server's own name for both kinds — nothing translates
                      a database row. */}
                <span className="block max-w-[6rem] text-pretty">
                  {role.name}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {modules.map((module) => (
            <Fragment key={module.id}>
              <tr>
                <th
                  scope="colgroup"
                  colSpan={roles.length + 1}
                  className="eyebrow sticky start-0 bg-card px-3.5 py-2 text-start"
                >
                  <span className="flex items-center gap-2">
                    <Icon name="layers" size="xs" className="text-ink-mute" />
                    {module.name}
                  </span>
                </th>
              </tr>
              {module.permissions.map((permission) => (
                <tr key={permission.id} className="border-t border-subtle">
                  <th
                    scope="row"
                    /* The description as a tooltip, for the same reason the
                         editor's pills carry one: a permission name alone does
                         not say what granting it allows. */
                    title={permission.description}
                    className="sticky start-0 bg-card px-3.5 py-2 ps-8 text-start text-body-sm font-normal text-ink-dim"
                  >
                    {permission.name}
                  </th>
                  {roles.map((role) => {
                    const granted =
                      grantedBy.get(role.id)?.has(permission.id) ?? false;
                    return (
                      <td key={role.id} className="px-2 py-2 text-center">
                        <span
                          title={t(
                            granted
                              ? "settings.roles.grantedOne"
                              : "settings.roles.withheld",
                          )}
                          className={cn(
                            "text-body-sm",
                            granted
                              ? "font-semibold text-on-green"
                              : "text-ink-mute",
                          )}
                        >
                          {granted ? "✓" : "—"}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </ForgeDialog>
  );
}
